/**
 * Settings page (admin only)
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { formatDate } from '../utils/format';
import CandidateCsvImportPanel from './CandidateCsvImportPanel';

export default function Settings() {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => (
    location.pathname.endsWith('/import') ? 'import' : (location.state?.activeTab || 'templates')
  ));

  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'manager' });
  const [userError, setUserError] = useState('');

  useEffect(() => {
    fetchTemplates();
    if (user?.role === 'admin') fetchUsers();
  }, [user?.role]);

  const fetchTemplates = async () => {
    try {
      const data = await api.get('/settings/templates');
      setTemplates(data.templates || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get('/settings/users');
      setUsers(data.users || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveTemplate = async (template) => {
    setTemplateLoading(true);
    try {
      await api.put('/settings/templates', { templates: [template] });
      await fetchTemplates();
      setEditingTemplate(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError('');
    try {
      await api.post('/settings/users', newUser);
      await fetchUsers();
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'manager' });
    } catch (err) {
      setUserError(err.message);
    }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await api.patch(`/settings/users/${userId}`, { role });
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-5 sm:px-5 lg:px-6">
        <div className="mb-5 rounded-card border border-border-default bg-white p-4 shadow-card">
          <h2 className="text-base font-bold text-text-primary">Workspace settings</h2>
          <p className="mt-1 text-sm text-text-muted">CSV import is first for launch; templates, users, and configuration remain available below.</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-1 overflow-x-auto rounded-app border border-border-default bg-surface-subtle p-1" role="tablist" aria-label="Settings sections">
          {[
            { key: 'templates', label: 'SMS Templates' },
            ...(user?.role === 'admin' ? [
              { key: 'import', label: 'CSV Import' },
              { key: 'users', label: 'Users' }
            ] : []),
            { key: 'config', label: 'Configuration' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`touch-target flex-shrink-0 rounded-md border px-4 py-2 text-sm font-semibold transition-colors focus-ring ${
                activeTab === tab.key
                  ? 'border-border-default bg-white text-brand-blue shadow-sm'
                  : 'border-transparent text-text-muted hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Templates tab */}
        {activeTab === 'templates' && (
          <div className="space-y-3">
            {templates.map(template => (
              <div key={template.id} className="bg-white rounded-xl border border-gray-200 p-4">
                {editingTemplate?.id === template.id ? (
                  <div className="space-y-3">
                    <div>
                      <label htmlFor={`template-name-${template.id}`} className="block text-xs font-medium text-gray-500 mb-1">Template Name</label>
                      <input
                        id={`template-name-${template.id}`}
                        value={editingTemplate.name}
                        onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                        className="input text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor={`template-body-${template.id}`} className="block text-xs font-medium text-gray-500 mb-1">
                        Message Body
                        <span className="text-gray-400 ml-1">({editingTemplate.body.length} chars)</span>
                      </label>
                      <textarea
                        id={`template-body-${template.id}`}
                        value={editingTemplate.body}
                        onChange={e => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                        rows={4}
                        className="input text-sm resize-none font-mono"
                      />
                      <TemplatePlaceholderPreview body={editingTemplate.body} />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveTemplate(editingTemplate)}
                        disabled={templateLoading}
                        className="touch-target px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium focus-ring"
                      >
                        {templateLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingTemplate(null)}
                        className="touch-target px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 focus-ring"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">{template.name}</h3>
                        <p className="text-xs text-gray-400 font-mono">{template.template_key}</p>
                      </div>
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => setEditingTemplate({ ...template })}
                          className="touch-target text-sm text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg focus-ring"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                      {template.body}
                    </p>
                    {template.description && (
                      <p className="text-xs text-gray-400 mt-1">{template.description}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CSV Import tab */}
        {activeTab === 'import' && user?.role === 'admin' && (
          <CandidateCsvImportPanel />
        )}

        {/* Users tab */}
        {activeTab === 'users' && user?.role === 'admin' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">User Accounts</h2>
              <button
                onClick={() => setShowAddUser(true)}
                className="touch-target px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium focus-ring"
              >
                + Add User
              </button>
            </div>

            <div className="space-y-3 sm:hidden">
              {users.map(u => (
                <div key={u.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{u.name}</p>
                      <p className="break-words text-sm text-gray-600">{u.email}</p>
                      <p className="mt-1 text-xs text-gray-500">Created {formatDate(u.created_at)}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {u.role}
                    </span>
                  </div>
                  {u.id !== user.id && (
                    <label className="mt-3 block text-sm font-medium text-gray-700">
                      Change role
                      <select
                        value={u.role}
                        onChange={e => handleChangeRole(u.id, e.target.value)}
                        className="input mt-1 text-sm bg-white focus-ring"
                      >
                        <option value="admin">admin</option>
                        <option value="manager">manager</option>
                      </select>
                    </label>
                  )}
                </div>
              ))}
            </div>

            <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white sm:block">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        {u.id !== user.id && (
                          <select
                            value={u.role}
                            onChange={e => handleChangeRole(u.id, e.target.value)}
                            className="touch-target rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:border-gray-400 focus-ring"
                          >
                            <option value="admin">admin</option>
                            <option value="manager">manager</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add user form */}
            {showAddUser && (
              <div className="mt-4 bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Add New User</h3>
                {userError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{userError}</div>
                )}
                <form onSubmit={handleAddUser} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="new-user-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      id="new-user-name"
                      value={newUser.name}
                      onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                      required
                      className="input text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-user-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      id="new-user-email"
                      type="email"
                      value={newUser.email}
                      onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                      required
                      className="input text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-user-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input
                      id="new-user-password"
                      type="password"
                      value={newUser.password}
                      onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                      required
                      className="input text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-user-role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      id="new-user-role"
                      value={newUser.role}
                      onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                      className="input text-sm bg-white focus-ring"
                    >
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2 flex gap-2 pt-1">
                    <button
                      type="submit"
                      className="touch-target px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium focus-ring"
                    >
                      Add User
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddUser(false)}
                      className="touch-target px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 focus-ring"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Config tab */}
        {activeTab === 'config' && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Integration Settings</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">SMS Provider</span>
                <span className="font-medium">ClickSend</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Mock SMS Mode</span>
                <span className={`font-medium ${import.meta.env.VITE_MOCK_SMS === 'true' ? 'text-amber-600' : 'text-green-600'}`}>
                  Active (development mode)
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Application Form</span>
                <a href="/apply" target="_blank" rel="noreferrer" className="touch-target inline-flex items-center justify-end rounded-lg px-2 text-blue-600 hover:underline focus-ring">
                  /apply ↗
                </a>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">ClickSend Webhook</span>
                <span className="font-mono text-xs text-gray-600">/api/webhooks/clicksend</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-500">Retell Webhook</span>
                <span className="font-mono text-xs text-gray-600">/api/webhooks/retell</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function TemplatePlaceholderPreview({ body }) {
  const parts = String(body || '').split(/(\[[a-z_]+\])/gi);

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
      <p className="mb-1 font-medium text-gray-500">Placeholder preview</p>
      <p className="whitespace-pre-wrap font-mono leading-5">
        {parts.map((part, index) => /^\[[a-z_]+\]$/i.test(part) ? (
          <span key={`${part}-${index}`} className="rounded bg-blue-100 px-1 font-semibold text-blue-800 ring-1 ring-blue-200">
            {part}
          </span>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        ))}
      </p>
    </div>
  );
}
